/**
 * Iter 45p — Log Session dialog.
 * FP3-035B-2 regression fix (iter 64) — Aligned with Document
 * Session so a provider documenting a scheduled session can also
 * address MULTIPLE goals per student in one pass.
 *
 * FP3-035E (Feb 17, 2026) — The per-student attendance + multi-goal
 * evidence markup (previously ~80% duplicated against
 * DocumentQuickSessionDialog) now lives in the shared
 * `SessionDocumentationRows` component. This dialog still owns:
 *
 *   - modal chrome + workspace sizing
 *   - scheduled-session header (start/end, service type, group badge)
 *   - group summary textarea
 *   - per-student state + goal fetch
 *   - submit pipeline (launchSessions → logSession)
 *
 * Multi-goal save shape unchanged: one entry per (student, goal) pair,
 * with the shared `student_note` echoed as narrative_note across every
 * row for that student. Absent / Unavailable students contribute 0
 * minutes and skip the goal-evidence section entirely.
 *
 * Launch points: TodayDashboard, Schedule Calendar (week + day cards).
 * Caller is responsible for skipping already-completed sessions and
 * disabling for future dates.
 *
 * Save flow (preserved from the old modal):
 *   1) /sessions/launch resolves any synthetic recurring-block
 *      ids (rb__<id>__<date>) into real session rows.
 *   2) /sessions/{id}/log is called once per real session with
 *      attendance + the multi-goal evidence array.
 *
 * Defensive event guards (FP3-035B-2 iter 63):
 *   - DialogContent stops propagation on click / pointerdown so
 *     Radix Portal SelectItem clicks can't leak back to the
 *     calendar card's onClick handler (which navigates to
 *     /session?launch=... — Live Session).
 *   - The calendar cards also early-return when `logOpen` is true.
 */
import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Users, ClipboardText } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { invalidateAfterMutation } from "@/lib/adminQueries";
import SessionDocumentationRows, {
  makeInitialStudentRow,
  toggleGoalInRow,
  updateGoalRowField,
} from "@/components/log/SessionDocumentationRows";

function formatTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":");
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  return `${hour % 12 || 12}:${m} ${ampm}`;
}

export default function LogSessionDialog({ open, onOpenChange, session, onLogged, mode = "log" }) {
  const [groupSummary, setGroupSummary] = useState("");
  // FP3-114 — Shared group note is now an opt-in disclosure to keep
  // per-student notes as the primary surface.
  const [showGroupNote, setShowGroupNote] = useState(false);
  // FP3-114A — Session-level outcome. "held" preserves the full
  // per-student flow; "provider_unavailable" documents that the
  // scheduled session did not occur because the provider was
  // unavailable and stores only one session-level note.
  const [sessionOutcome, setSessionOutcome] = useState("held");
  const [providerUnavailableNote, setProviderUnavailableNote] = useState("");
  // FP3-035B-2 iter 64 — Per-student row shape mirrored from
  // DocumentQuickSessionDialog: { attendance, student_note,
  // goal_rows: [{ goal_id, entry_type, numerator, denominator,
  // assessment_label }, ...] }. FP3-035E moved the actual render
  // markup into SessionDocumentationRows; this dialog only owns
  // state + save pipeline now.
  const [perStudent, setPerStudent] = useState({});
  const [goalsByStudent, setGoalsByStudent] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // FP3-122 — In `edit` mode, the dialog is opened for an already
  // completed session. We skip `sessions/launch` (no synthetic ids
  // to resolve) and log directly against the real session row so
  // the backend can archive prior documentation into
  // `correction_history[]` and mark stale evidence superseded.
  const isEditMode = mode === "edit";
  const students = useMemo(() => session?.students || [], [session]);

  // Reset on each open; pull active goals for each student so the
  // inline goal-picker has options. Fails soft — empty list still
  // permits attendance + narrative only.
  useEffect(() => {
    if (!open || !session) return;
    // FP3-122 — Seed from the existing session when editing. Held
    // sessions restore attendance + per-student notes + group
    // summary; PU sessions restore the provider-unavailability note.
    if (isEditMode) {
      const priorOutcome =
        (session.session_outcome || "held").toLowerCase() === "provider_unavailable"
          ? "provider_unavailable"
          : "held";
      setSessionOutcome(priorOutcome);
      setProviderUnavailableNote(session.provider_unavailable_note || "");
      setGroupSummary(session.session_note || "");
      setShowGroupNote(!!(session.session_note || "").trim());
      const priorAttendance = session.student_attendance || {};
      const priorNotes = session.student_notes || {};
      const initial = {};
      students.forEach((s) => {
        initial[s.id] = {
          ...makeInitialStudentRow(),
          attendance: priorAttendance[s.id] || "present",
          student_note: priorNotes[s.id] || "",
        };
      });
      setPerStudent(initial);
    } else {
      setGroupSummary("");
      setShowGroupNote(false);
      setSessionOutcome("held");
      setProviderUnavailableNote("");
      const initial = {};
      students.forEach((s) => {
        initial[s.id] = makeInitialStudentRow();
      });
      setPerStudent(initial);
    }
    Promise.all(
      students.map((s) =>
        api
          .getStudent(s.id)
          .then((r) => [s.id, (r.data?.goals || []).filter((g) => g.status === "active")])
          .catch(() => [s.id, []]),
      ),
    ).then((rows) => {
      const map = {};
      rows.forEach(([sid, goals]) => {
        map[sid] = goals;
      });
      setGoalsByStudent(map);
    });
  }, [open, session, students, isEditMode]);

  // ── State helpers — narrow callbacks the shared rows component
  // invokes. Defined here so each dialog can keep its own state
  // ownership while reusing the same UI.

  const setAttendance = (sid, value) => {
    setPerStudent((prev) => ({
      ...prev,
      [sid]: { ...(prev[sid] || makeInitialStudentRow()), attendance: value },
    }));
  };

  const setStudentNote = (sid, value) => {
    setPerStudent((prev) => ({
      ...prev,
      [sid]: { ...(prev[sid] || makeInitialStudentRow()), student_note: value },
    }));
  };

  const toggleGoal = (sid, goalId) => {
    setPerStudent((prev) => ({
      ...prev,
      [sid]: toggleGoalInRow(prev[sid], goalId),
    }));
  };

  const setGoalRowField = (sid, goalId, field, value) => {
    setPerStudent((prev) => {
      const cur = prev[sid];
      if (!cur) return prev;
      return { ...prev, [sid]: updateGoalRowField(cur, goalId, field, value) };
    });
  };

  const onMarkAllPresent = () => {
    setPerStudent((prev) => {
      const next = { ...prev };
      for (const s of students) {
        next[s.id] = { ...(prev[s.id] || makeInitialStudentRow()), attendance: "present" };
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!session) return;
    setSubmitting(true);
    try {
      // FP3-122 — In edit mode the session is already real and
      // completed. Skip `sessions/launch` (which was designed to
      // convert synthetic rb__<id>__<date> ids into real rows) and
      // log directly against the existing row. The backend detects
      // the correction, archives the prior state into
      // `correction_history[]`, and marks stale evidence superseded.
      let realSessions;
      if (isEditMode) {
        realSessions = [session];
      } else {
        // Resolve synthetic ids first so we have real session rows.
        const sourceIds =
          session.session_ids && session.session_ids.length > 0
            ? session.session_ids
            : [session.id];
        const launched = await api.launchSessions({ session_ids: sourceIds });
        realSessions = launched.data?.sessions || [];
      }
      if (realSessions.length === 0) {
        toast.error("Could not resolve session for logging");
        setSubmitting(false);
        return;
      }
      let totalEvidence = 0;
      for (const real of realSessions) {
        // FP3-114A — Session-level Provider Unavailable outcome
        // short-circuits the per-student evidence build. No
        // attendance / goal / student notes are sent — the backend
        // stores the outcome and provider note only, and awards
        // zero delivered minutes to every participant.
        if (sessionOutcome === "provider_unavailable") {
          const res = await api.logSession(real.id, {
            session_id: real.id,
            session_outcome: "provider_unavailable",
            provider_unavailable_note: providerUnavailableNote.trim(),
            students: [],
          });
          totalEvidence += res.data?.evidence_written || 0;
          continue;
        }

        // FP3-035B-2 iter 64 — Build per-student log entries. When a
        // student has multiple selected goals we send ONE entry per
        // (student, goal) pair. The shared `student_note` becomes
        // the narrative_note on every goal row so the goal-timeline
        // surfaces the same observation under each addressed goal.
        const studentEntries = [];
        for (const sid of (real.student_ids || [])) {
          const row = perStudent[sid] || {};
          const attendance = row.attendance || "present";
          const sharedNote = (row.student_note || "").trim();
          const goalRows = attendance === "present" ? (row.goal_rows || []) : [];
          if (goalRows.length === 0) {
            // No goals (or not present) — single attendance-only entry.
            studentEntries.push({
              student_id: sid,
              attendance,
              student_note: sharedNote,
            });
            continue;
          }
          for (const g of goalRows) {
            const entry = {
              student_id: sid,
              attendance,
              student_note: sharedNote,
              goal_id: g.goal_id,
              entry_type: g.entry_type || "narrative_observation",
            };
            const n = parseInt(g.numerator, 10);
            const d = parseInt(g.denominator, 10);
            if (Number.isFinite(n)) entry.numerator = n;
            if (Number.isFinite(d)) entry.denominator = d;
            if (g.assessment_label) entry.assessment_label = g.assessment_label;
            studentEntries.push(entry);
          }
        }
        const res = await api.logSession(real.id, {
          session_id: real.id,
          session_outcome: "held",
          group_summary: groupSummary,
          students: studentEntries,
        });
        totalEvidence += res.data?.evidence_written || 0;
      }
      toast.success(
        isEditMode
          ? sessionOutcome === "provider_unavailable"
            ? "Session corrected · marked provider unavailable"
            : `Session corrected${totalEvidence > 0 ? ` · ${totalEvidence} progress ${totalEvidence === 1 ? "entry" : "entries"} saved` : ""}`
          : sessionOutcome === "provider_unavailable"
            ? "Provider unavailability logged"
            : totalEvidence > 0
              ? `Session logged · ${totalEvidence} progress ${totalEvidence === 1 ? "entry" : "entries"} saved`
              : "Session logged",
      );
      onOpenChange(false);
      // FP3-120 — Session write invalidates admin dashboard Today
      // counts, progress, coverage, and reports caches.
      invalidateAfterMutation.sessionLogged();
      if (onLogged) onLogged();
    } catch (err) {
      console.error(err);
      // FP3-122 — Surface backend-provided detail when present so
      // the guard rails ("Provider unavailable can only be logged
      // for a scheduled session…") reach the provider instead of
      // the generic "Could not log session — please try again".
      const detail = err?.response?.data?.detail;
      const fallback = isEditMode
        ? "Could not save correction — please try again"
        : "Could not log session — please try again";
      toast.error(typeof detail === "string" && detail.trim() ? detail : fallback);
    } finally {
      setSubmitting(false);
    }
  };

  if (!session) return null;
  const isGroup = students.length > 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // FP3-035B-2 iter 64 — Workspace-sized modal aligned with
        // Document Session. Multi-goal evidence cards need real
        // horizontal room; the old sm:max-w-2xl was way too narrow.
        className="w-[95vw] sm:w-[92vw] sm:max-w-[1200px] max-h-[90vh] p-0 bg-[#FBFAF6] flex flex-col gap-0 overflow-hidden"
        data-testid="log-session-dialog"
        // FP3-035B-2 iter 63 — Defensive stopPropagation so Radix
        // Portal SelectItem clicks can't leak back to the calendar
        // card's onClick (which navigates to /session?launch=...).
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-[#ECEAE4] shrink-0">
          <DialogTitle className="text-[#1F2937] heading-font flex items-center gap-2">
            {isGroup ? <Users size={18} weight="duotone" /> : <ClipboardText size={18} weight="duotone" />}
            {isEditMode ? "Edit Session" : "Log Session"}
          </DialogTitle>
          {/* FP3-035E follow-up — visually hidden description so
              Radix Dialog stops warning about missing aria
              description. The visible header below still carries
              the human-facing context. */}
          <DialogDescription className="sr-only">
            {isEditMode
              ? "Correct this session's outcome, attendance, or notes. Prior documentation is preserved in the correction history."
              : "Document attendance and per-goal evidence for this scheduled session."}
          </DialogDescription>
          <div className="flex items-center gap-2 text-xs text-[#6B7280] mt-1">
            <span>{formatTime(session.start_time)} – {formatTime(session.end_time)}</span>
            {session.service_type && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-[#E5E3DB] text-[#6B7280]">
                {session.service_type}
              </Badge>
            )}
            {isGroup && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-[#1E3F33]/30 bg-[#E8F0EB] text-[#1E3F33]">
                group · {students.length}
              </Badge>
            )}
          </div>
        </DialogHeader>

        <div
          data-testid="log-session-body"
          className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-3"
        >
          {/* FP3-114A — Session-level outcome picker. Documents at
              the top of the modal whether the session actually
              occurred. `provider_unavailable` short-circuits the
              per-student surface and only writes a single session
              note explaining why the session could not be delivered.
              Never labels any student absent / unavailable. */}
          <div data-testid="log-outcome-picker" className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-wide text-[#6B7280] font-medium mr-1">
              Session outcome
            </span>
            {[
              { value: "held", label: "Session held" },
              { value: "provider_unavailable", label: "Provider unavailable" },
            ].map((opt) => {
              const active = sessionOutcome === opt.value;
              // FP3-122 — Provider Unavailable is only valid for
              // sessions tied to a recurring block occurrence (see
              // FP3-114B backend guard). In edit mode we can detect
              // ad-hoc sessions (no `_recurring_block_id`) and
              // disable the PU button so the surface never invites
              // the 422 rejection path. ONLY the PU outcome is
              // gated — Session Held remains fully editable
              // (attendance, per-student notes, group summary, and
              // goal evidence) for ad-hoc sessions.
              const pursSchedule = opt.value === "provider_unavailable";
              const puDisabled =
                pursSchedule &&
                isEditMode &&
                !session?._recurring_block_id;
              return (
                <button
                  key={opt.value}
                  type="button"
                  data-testid={`log-outcome-${opt.value}`}
                  onClick={() => {
                    if (puDisabled) return;
                    setSessionOutcome(opt.value);
                  }}
                  disabled={puDisabled}
                  title={
                    puDisabled
                      ? "Provider Unavailable is only available for sessions scheduled from a recurring block. You can still update attendance, notes, and goal evidence on this ad-hoc session."
                      : undefined
                  }
                  className={`px-2.5 py-1 rounded-md text-[11px] font-medium border transition ${
                    puDisabled
                      ? "bg-[#F3F1EC] text-[#9B9890] border-[#E5E3DB] cursor-not-allowed"
                      : active
                        ? opt.value === "provider_unavailable"
                          ? "bg-[#9C6B2E] text-white border-[#9C6B2E]"
                          : "bg-[#1E3F33] text-white border-[#1E3F33]"
                        : "bg-white text-[#6B7280] border-[#E5E3DB] hover:border-[#9B9890]"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          {/* FP3-122A — Ad-hoc edit clarity banner. Renders only in
              edit mode for a session with no `_recurring_block_id`
              so the provider immediately understands that
              attendance / notes / evidence remain fully editable —
              the PU button is the ONE gated thing, not the row. */}
          {isEditMode && !session?._recurring_block_id && (
            <p
              data-testid="edit-adhoc-hint"
              className="text-[11px] text-[#7D6340] bg-[#FDF8F3] border border-[#E5D7B7] rounded px-2 py-1"
            >
              This is an ad-hoc session. You can still correct attendance, per-student notes, the shared note, and goal evidence — only the Provider Unavailable outcome is limited to sessions scheduled from a recurring block.
            </p>
          )}

          {sessionOutcome === "provider_unavailable" ? (
            <div
              data-testid="log-provider-unavailable-panel"
              className="rounded-xl border border-[#E7D6B4] bg-[#FBF6EC] p-4 space-y-2"
            >
              <p className="text-[12px] text-[#5A3E12] leading-snug">
                Session was not provided because the provider was unavailable.
                No student receives delivered minutes for this scheduled slot,
                and no student is marked absent or unavailable.
              </p>
              <div>
                <Label className="text-[11px] font-medium text-[#5A3E12] mb-1 block">
                  Provider unavailability note
                </Label>
                <Textarea
                  data-testid="log-provider-unavailable-note"
                  value={providerUnavailableNote}
                  onChange={(e) => setProviderUnavailableNote(e.target.value)}
                  placeholder="Briefly document why the session could not be provided."
                  className="min-h-[80px] text-sm bg-white"
                />
                <p className="text-[10px] text-[#6B7280] mt-1">
                  This note is stored on the session and shown to every participant's Session History.
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* FP3-114 — Shared group note is now an opt-in disclosure.
                  Individual student notes are the primary way to capture
                  observations; the shared field is optional. For an
                  individual session (`!isGroup`) we hide the affordance
                  entirely because the per-student note is sufficient. */}
              {isGroup && (
                <div>
                  {!showGroupNote && (
                    <button
                      type="button"
                      onClick={() => setShowGroupNote(true)}
                      data-testid="log-add-group-note"
                      className="text-xs font-medium text-[#1E3F33] hover:underline"
                    >
                      + Add shared group note
                    </button>
                  )}
                  {showGroupNote && (
                    <div data-testid="log-group-note-wrapper">
                      <Label className="text-xs font-medium text-[#1F2937] mb-1 block">
                        Shared group note
                      </Label>
                      <Textarea
                        data-testid="log-group-summary"
                        value={groupSummary}
                        onChange={(e) => setGroupSummary(e.target.value)}
                        placeholder="What did this session focus on? Any notable observations for the whole group?"
                        className="min-h-[70px] text-sm"
                      />
                      <p className="text-[10px] text-[#6B7280] mt-1">
                        This optional note applies to the overall session. Student-specific details belong in each student's note.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Per-student rows — extracted to SessionDocumentationRows
                  in FP3-035E so this dialog and DocumentQuickSessionDialog
                  share one source of truth for the attendance + goal
                  evidence markup. */}
              <SessionDocumentationRows
                students={students}
                perStudent={perStudent}
                goalsByStudent={goalsByStudent}
                testIdPrefix="log"
                setAttendance={setAttendance}
                setStudentNote={setStudentNote}
                toggleGoal={toggleGoal}
                setGoalRowField={setGoalRowField}
                onMarkAllPresent={onMarkAllPresent}
              />
            </>
          )}
        </div>

        <DialogFooter className="px-6 py-3 border-t border-[#ECEAE4] bg-[#F6F4EE] shrink-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="log-session-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="log-session-submit"
            className={
              sessionOutcome === "provider_unavailable"
                ? "bg-[#9C6B2E] hover:bg-[#7A5220] text-white"
                : "bg-[#1E3F33] hover:bg-[#173127] text-white"
            }
          >
            {submitting
              ? "Saving..."
              : isEditMode
                ? sessionOutcome === "provider_unavailable"
                  ? "Save correction · Provider unavailable"
                  : "Save correction"
                : sessionOutcome === "provider_unavailable"
                  ? "Log provider unavailable"
                  : "Log Session"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
